import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { HumanoidRobot, DBRobot, DBRobotUpdate } from "@/types";

// Re-export types for backward compatibility
export type { HumanoidRobot } from "@/types";

// Helper to extract primitive values from specs that may contain {unit, value} objects
const extractSpecValue = (spec: unknown): string | number | undefined => {
  if (spec === null || spec === undefined) return undefined;
  if (typeof spec === 'string' || typeof spec === 'number') return spec;
  if (typeof spec === 'object' && spec !== null) {
    const obj = spec as Record<string, unknown>;
    // Handle {value, unit} format - combine them into a string
    if ('value' in obj) {
      const value = obj.value;
      const unit = 'unit' in obj ? obj.unit : '';
      if (value !== null && value !== undefined) {
        return unit ? `${value} ${unit}` : String(value);
      }
    }
    // Handle {overall: {value}} nested format
    if ('overall' in obj) {
      return extractSpecValue(obj.overall);
    }
  }
  return undefined;
};

// Transform raw specs from DB to normalized format
const transformSpecs = (rawSpecs: unknown): HumanoidRobot['specs'] => {
  if (!rawSpecs || typeof rawSpecs !== 'object') {
    return { height: '', weight: '', dof: 0 };
  }
  
  const specs = rawSpecs as Record<string, unknown>;
  
  const heightVal = extractSpecValue(specs.height) ?? extractSpecValue(specs.height_cm);
  const weightVal = extractSpecValue(specs.weight) ?? extractSpecValue(specs.weight_kg);
  const dofVal = extractSpecValue(specs.dof) ?? extractSpecValue(specs.degrees_of_freedom) ?? extractSpecValue(specs.degrees_of_freedom_overall);
  const payloadVal = extractSpecValue(specs.payload) ?? extractSpecValue(specs.payload_capacity) ?? extractSpecValue(specs.strength_payload_kg);
  const runtimeVal = extractSpecValue(specs.runtime) ?? extractSpecValue(specs.battery_runtime) ?? extractSpecValue(specs.runtime_pr_charge) ?? extractSpecValue(specs.runtime_pr_charge_hours);
  const speedVal = extractSpecValue(specs.speed) ?? extractSpecValue(specs.max_speed) ?? extractSpecValue(specs.walking_speed);
  
  return {
    height: heightVal !== undefined ? String(heightVal) : '',
    weight: weightVal !== undefined ? String(weightVal) : '',
    dof: typeof dofVal === 'number' ? dofVal : (typeof dofVal === 'string' ? parseInt(dofVal, 10) || 0 : 0),
    payload: payloadVal !== undefined ? String(payloadVal) : undefined,
    runtime: runtimeVal !== undefined ? String(runtimeVal) : undefined,
    speed: speedVal !== undefined ? String(speedVal) : undefined,
  };
};

const transformRobot = (robot: DBRobot, updates: DBRobotUpdate[] = []): HumanoidRobot => ({
  id: robot.id,
  name: robot.name,
  company: robot.company,
  country: robot.country,
  region: robot.region,
  year: robot.year,
  status: robot.status as HumanoidRobot['status'],
  useCases: robot.use_cases,
  autonomyLevel: robot.autonomy_level,
  specs: transformSpecs(robot.specs),
  aiCapabilities: robot.ai_capabilities,
  description: robot.description,
  about: robot.about,
  images: robot.images,
  website: robot.website || undefined,
  priceRange: robot.price_range || undefined,
  availability: robot.availability || undefined,
  targetMarkets: robot.target_markets,
  unitsDeployed: robot.units_deployed || undefined,
  lastUpdated: robot.last_updated,
  updates: updates.map(u => ({
    title: u.title,
    description: u.description,
    date: u.date,
    source: u.source || undefined,
  })),
  
  // Physical & Hardware
  locomotionType: robot.locomotion_type || undefined,
  maxSpeed: robot.max_speed || undefined,
  fingersPerHand: robot.fingers_per_hand || undefined,
  gripStrength: robot.grip_strength || undefined,
  armDof: robot.arm_dof || undefined,
  actuatorType: robot.actuator_type || undefined,
  actuatorCount: robot.actuator_count || undefined,
  batteryType: robot.battery_type || undefined,
  chargingTime: robot.charging_time || undefined,
  sensors: robot.sensors || undefined,
  cameras: robot.cameras || undefined,
  lidar: robot.lidar || undefined,
  depthSensors: robot.depth_sensors || undefined,
  tactileSensors: robot.tactile_sensors || undefined,
  onboardCompute: robot.onboard_compute || undefined,
  cloudDependency: robot.cloud_dependency || undefined,
  
  // Software & AI
  visionModel: robot.vision_model || undefined,
  languageModel: robot.language_model || undefined,
  motionPlanning: robot.motion_planning || undefined,
  proprietaryModels: robot.proprietary_models || undefined,
  openSourceModels: robot.open_source_models || undefined,
  aiPartners: robot.ai_partners || undefined,
  operatingSystem: robot.operating_system || undefined,
  middleware: robot.middleware || undefined,
  preTrained: robot.pre_trained ?? undefined,
  onDeviceLearning: robot.on_device_learning || undefined,
  cloudLearning: robot.cloud_learning || undefined,
  
  // Use Cases & Environment
  homeCompatible: robot.home_compatible || undefined,
  officeCompatible: robot.office_compatible || undefined,
  factoryCompatible: robot.factory_compatible || undefined,
  voiceInteraction: robot.voice_interaction || undefined,
  touchInteraction: robot.touch_interaction || undefined,
  gestureInteraction: robot.gesture_interaction || undefined,
  facialExpressionInteraction: robot.facial_expression_interaction || undefined,
  
  // Ecosystem & Platform
  sdkAvailable: robot.sdk_available || undefined,
  apiAccess: robot.api_access || undefined,
  appSystem: robot.app_system || undefined,
  swappableComponents: robot.swappable_components || undefined,
  toolAttachments: robot.tool_attachments || undefined,
  integrations: robot.integrations || undefined,
  softwareUpdates: robot.software_updates || undefined,
  hardwareUpgrades: robot.hardware_upgrades || undefined,
  
  // Commercial & Strategic
  saleModel: robot.sale_model || undefined,
  leaseModel: robot.lease_model || undefined,
  raasModel: robot.raas_model || undefined,
  keyPartners: robot.key_partners || undefined,
  competitors: robot.competitors || undefined,
  strategicPositioning: robot.strategic_positioning || undefined,
  
  // Media & References
  videos: robot.videos || undefined,
  pressReleases: robot.press_releases || undefined,
  whitepapers: robot.whitepapers || undefined,
  
  // Data quality tracking
  estimatedFields: robot.estimated_fields || [],
  
  // Approval status
  isApproved: robot.is_approved,
  
  // Featured status
  isFeatured: robot.is_featured,
  
  // Version
  currentVersion: robot.current_version || undefined,
  
  // Hero image
  heroImage: robot.hero_image || undefined,
});

export const useRobots = () => {
  return useQuery({
    queryKey: ['robots'],
    queryFn: async (): Promise<HumanoidRobot[]> => {
      const { data: robots, error } = await supabase
        .from('humanoid_robots')
        .select('*')
        .order('name');

      if (error) throw error;
      if (!robots) return [];

      return robots.map(r => transformRobot(r));
    },
  });
};

export const useRobot = (id: string | undefined) => {
  return useQuery({
    queryKey: ['robot', id],
    queryFn: async (): Promise<HumanoidRobot | null> => {
      if (!id) return null;

      const { data: robot, error: robotError } = await supabase
        .from('humanoid_robots')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (robotError) throw robotError;
      if (!robot) return null;

      const { data: updates, error: updatesError } = await supabase
        .from('robot_updates')
        .select('*')
        .eq('robot_id', id)
        .order('date', { ascending: false });

      if (updatesError) throw updatesError;

      return transformRobot(robot, updates || []);
    },
    enabled: !!id,
  });
};

export const getUniqueUseCases = (robots: HumanoidRobot[]): string[] => {
  const allUseCases = robots.flatMap(r => r.useCases);
  return [...new Set(allUseCases)].sort();
};

export const getUniqueCompanies = (robots: HumanoidRobot[]): string[] => {
  return [...new Set(robots.map(r => r.company))].sort();
};

export const getUniqueStatuses = (robots: HumanoidRobot[]): string[] => {
  return [...new Set(robots.map(r => r.status))];
};
